import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectHexes,
  selectPendingSupply,
  selectPhase,
  selectPlayers,
  selectPlayerUnits,
  selectSelectedFarmsForConversion,
  selectSpecialBuildings,
  selectUnits,
} from '../../../redux/reducers/sharedStateReducer/selectors';
import {selectConstructionPlanEntries} from '../../../redux/reducers/privateStateReducer/selectors';
import {PHASES} from '../../../constants/phases';
import {getFarmsOfPlayerCities} from '../getPlayerFarms';
import {calcWorkingFarmsSupply} from '../supply/calcWorkingFarmsSupply';
import {calculateCitiesCache} from '../calculateCitiesCache';
import {applyConstructionPlanEntriesToField} from '../construction/planning/buildConstructionPlanningViewState';
import {
  CONSTRUCTION_PLAN_CONTEXTS,
  getConstructionPlanContext,
  getConstructionPlanPlayer,
} from '../construction/planning/getConstructionPlanContext';

const EMPTY_PLAN_ENTRIES = Object.freeze([]);

// Кэш на одну запись: числа плашки пересчитываются на каждое изменение стора, а
// их входы (ячейки, спецстроения, план) меняются заметно реже. Дороже всего
// здесь обход поля в calculateCitiesCache
let cachedConstructionField = null;

/**
 * Поле фазы строительства таким, каким его уже видит игрок: с тем, что он
 * построил в этом ходу, и с планом, если тот еще не применен.
 *
 * Кэш городов в сторе пересчитывается только на завершении хода и построенного
 * в текущем ходу не видит: по нему ферма, поставленная минуту назад, в город
 * игрока еще не входит и снабжения как бы не дает. Поэтому здесь он считается
 * заново по актуальным ячейкам.
 */
const getConstructionPhaseField = (hexes, specialBuildings, planEntries) => {
  if (cachedConstructionField
    && cachedConstructionField.hexes === hexes
    && cachedConstructionField.specialBuildings === specialBuildings
    && cachedConstructionField.planEntries === planEntries
  ) {
    return cachedConstructionField.field;
  }

  const planField = planEntries.length
    ? applyConstructionPlanEntriesToField(hexes, specialBuildings, planEntries)
    : {hexes, specialBuildings};
  const field = {
    ...planField,
    citiesCache: calculateCitiesCache(planField.hexes, planField.specialBuildings),
  };

  cachedConstructionField = {hexes, specialBuildings, planEntries, field};
  return field;
};

/**
 * Записи плана строительства, если они относятся к этому игроку и еще не
 * применены. В свой ход план применяется не мгновенно (constructionPlanMiddleware
 * показывает сообщение), да и повторное наложение уже построенного ничего не
 * меняет: жетон в ячейке тот же
 */
const getPlanEntriesOfPlayer = (state, player) => {
  const hasPlanContext = getConstructionPlanContext(state) !== CONSTRUCTION_PLAN_CONTEXTS.NONE;
  const isPlanOfThisPlayer = hasPlanContext && getConstructionPlanPlayer(state)?.id === player.id;
  return isPlanOfThisPlayer ? selectConstructionPlanEntries(state) : EMPTY_PLAN_ENTRIES;
};

/**
 * Два числа плашки этапа найма (RecruitmentSummary): сколько у игрока отрядов
 * и сколько снабжения дадут все его рабочие фермы. Один расчет на всех, чтобы
 * плашка и подсказка после найма («кто-то будет голодать») не разошлись.
 *
 * В фазе Строительства в снабжение попадают и фермы этого хода — игрок как раз
 * и смотрит на плашку, решая, хватит ли ферм под будущие отряды.
 *
 * @param {Object} state
 * @param {Object} player - игрок, за которого считаем
 * @returns {{unitsCount: number, supply: number}}
 */
export const calcPlayerUnitsAndSupply = (state, player) => {
  if (!player) {
    return {unitsCount: 0, supply: 0};
  }

  const storeHexes = selectHexes(state);
  const storeSpecialBuildings = selectSpecialBuildings(state);

  // Только в строительстве: в остальных фазах ход уже завершался, и кэш городов
  // в сторе построенное в нем видит
  const {hexes, specialBuildings, citiesCache} = selectPhase(state) === PHASES.CONSTRUCTION
    ? getConstructionPhaseField(storeHexes, storeSpecialBuildings, getPlanEntriesOfPlayer(state, player))
    : {hexes: storeHexes, specialBuildings: storeSpecialBuildings, citiesCache: selectCitiesCache(state)};

  const supply = calcWorkingFarmsSupply(getFarmsOfPlayerCities(citiesCache, player.color), {
    selectedFarmsForConversion: selectSelectedFarmsForConversion(state, player.id),
    hexes,
    specialBuildings,
    players: selectPlayers(state),
    units: selectUnits(state),
    citiesCache,
    currentPlayer: player,
    pendingSupply: selectPendingSupply(state),
    buildingsDamage: selectBuildingsDamage(state),
  });

  return {unitsCount: selectPlayerUnits(state, player.id).length, supply};
};

/**
 * Отрядов стало больше, чем снабжения: в следующей фазе Снабжения кому-то пайка
 * не хватит
 *
 * @param {Object} state
 * @param {Object} player
 * @returns {boolean}
 */
export const checkIfPlayerUnitsExceedSupply = (state, player) => {
  const {unitsCount, supply} = calcPlayerUnitsAndSupply(state, player);
  return unitsCount > supply;
};
